import { ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import EnhancedDynamicTablePagination from "../../components/common/CommonTable/EnhancedDynamicTablePagination";
import CommonButton from "../../components/common11/Buttons/CommonButton";
import CommonDatePicker from "../../components/common11/formInputs/CommonDatePicker";
import InputField from "../../components/common11/formInputs/InputField";
import RadioButtonGroup from "../../components/common11/formInputs/RadioButtonGroup";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "../../components/common11/toastUtils";
import {
  approvePathologyResultEntry,
  fetchPathologyLabs,
  fetchPathologyResultDetail,
  fetchPathologyResults,
  savePathologyResultEntry,
  setPathologyFilters,
  setSelectedPathologyId,
} from "../../store/slices/pathologyResultSlice";
import PathologyResultEntryModal from "./PathologyResultEntryModal";
import { getDefaultFilters } from "./pathologyResultApprovalData";

const PATIENT_TYPE_OPTIONS = [
  { label: "All", value: "All" },
  { label: "OP", value: "OP" },
  { label: "IP", value: "IP" },
];

const REPORT_STATUS_OPTIONS = [
  { label: "All", value: "All" },
  { label: "Pend.", value: "Pending" },
  { label: "Comp.", value: "Completed" },
];

const formatDisplayDate = (isoDate) => {
  if (!isoDate) return "—";
  const [y, m, d] = String(isoDate).split("-");
  if (!y || !m || !d) return isoDate;
  return `${d}/${m}/${y}`;
};

function PathologyResultApproval() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const {
    filters,
    tests,
    labs,
    selectedId,
    selectedDetail,
    loading,
    detailLoading,
    saving,
    approving,
  } = useSelector((state) => state.pathologyResult);

  const defaults = getDefaultFilters();
  const [modalOpen, setModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: filters || defaults,
  });

  useEffect(() => {
    dispatch(fetchPathologyResults(filters || defaults));
    dispatch(fetchPathologyLabs());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load
  }, [dispatch]);

  const tableData = useMemo(
    () =>
      (tests || []).map((t) => ({
        id: t.id,
        date: formatDisplayDate(t.date),
        time: t.time,
        uhid: t.uhid,
        patientName: t.patientName,
        testName: t.testName,
        doctorName: t.doctorName,
        reportStatus: t.reportStatus,
        printed: t.printed,
        approved: t.approved,
      })),
    [tests]
  );

  const selectedRecord = useMemo(() => {
    if (selectedDetail && selectedDetail.id === selectedId) {
      return selectedDetail;
    }
    return tests.find((t) => t.id === selectedId) || null;
  }, [tests, selectedId, selectedDetail]);

  const handleSearch = async (data) => {
    const next = { ...data };
    dispatch(setPathologyFilters(next));
    setPage(0);
    dispatch(setSelectedPathologyId(null));
    const result = await dispatch(fetchPathologyResults(next));
    if (fetchPathologyResults.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to load pathology results");
      return;
    }
    showInfoToast("Pathology list filtered.", { duration: 1500 });
  };

  const handleResetFilters = async () => {
    const next = getDefaultFilters();
    reset(next);
    dispatch(setPathologyFilters(next));
    setPage(0);
    dispatch(setSelectedPathologyId(null));
    const result = await dispatch(fetchPathologyResults(next));
    if (fetchPathologyResults.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to load pathology results");
    }
  };

  const handleRowClick = (row) => {
    dispatch(setSelectedPathologyId(row.id));
  };

  const handleResultEntry = async () => {
    if (!selectedId) {
      showInfoToast("Please select a test row first.");
      return;
    }
    const result = await dispatch(fetchPathologyResultDetail(selectedId));
    if (fetchPathologyResultDetail.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to load result entry");
      return;
    }
    setModalOpen(true);
  };

  const handleSaveResults = async (updated) => {
    const result = await dispatch(
      savePathologyResultEntry({
        record: updated,
        userId: authUser?.userId || 1,
      })
    );
    if (savePathologyResultEntry.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to save results");
      return;
    }
    setModalOpen(false);
    showSuccessToast(`Results saved for ${updated.patientName || "patient"}.`);
    dispatch(fetchPathologyResults(filters || defaults));
  };

  const handleApprove = async () => {
    if (!selectedId) {
      showInfoToast("Please select a test to approve.");
      return;
    }
    const result = await dispatch(
      approvePathologyResultEntry({
        pathReportId: selectedId,
        userId: authUser?.userId || 1,
      })
    );
    if (approvePathologyResultEntry.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to approve");
      return;
    }
    showSuccessToast("Test approved successfully.");
  };

  const renderCustomColumns = {
    date: (row) => row.date || "—",
    time: (row) => row.time || "—",
    uhid: (row) => row.uhid || "—",
    patientName: (row) => row.patientName || "—",
    testName: (row) => row.testName || "—",
    doctorName: (row) => row.doctorName || "—",
    reportStatus: (row) => (
      <span
        className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${
          row.reportStatus === "Completed"
            ? "bg-green-100 text-green-800 border-green-300"
            : "bg-amber-100 text-amber-800 border-amber-300"
        }`}
      >
        {row.reportStatus || "Pending"}
      </span>
    ),
  };

  const rowBackgroundColor = (row) => {
    if (selectedId === row.id) return "#F0F8FF";
    if (row.printed) return "#ECFDF5";
    if (row.reportStatus === "Completed") return "#FFF7ED";
    return "#fff";
  };

  const hasSelection = Boolean(selectedId);
  const isApproved = Boolean(selectedRecord?.approved);

  return (
    <div className="p-2 sm:p-3 h-[calc(100vh-3rem)] max-h-[calc(100vh-3rem)] flex flex-col overflow-hidden pb-16">
      <div className="bg-white rounded-lg shadow-sm border flex flex-col min-h-0 flex-1 overflow-hidden">
        <div className="sticky top-0 z-10 bg-white rounded-t-lg px-2 py-1 sm:px-3 sm:py-1.5 border-b border-gray-200 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm sm:text-base font-semibold text-[#217E90] flex items-center gap-1 sm:gap-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="p-0.5 rounded hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-[#217E90]"
                aria-label="Go back"
              >
                <ArrowUturnLeftIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
              </button>
              <span className="truncate">Pathology Result Approval</span>
            </h2>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden bg-[#217E90] text-white px-3 py-2 rounded-lg text-sm font-medium"
            >
              {showFilters ? "Hide" : "Show"} Filters
            </button>
          </div>
        </div>

        <div className="p-2 sm:p-3 flex-1 min-h-0 flex flex-col space-y-3 overflow-hidden">
          <form onSubmit={handleSubmit(handleSearch)} className="flex-shrink-0">
            <div
              className={`${
                showFilters ? "block" : "hidden"
              } lg:block rounded-lg border border-gray-200`}
            >
              <div className="p-3 sm:p-4">
                <div className="flex flex-nowrap items-end gap-2 sm:gap-3 w-full overflow-x-auto">
                  <div className="w-[16rem] sm:w-[18rem] lg:w-[20rem] shrink-0 min-w-0">
                    <InputField
                      name="search"
                      control={control}
                      label=""
                      placeholder="Search by patient name, sample no"
                      fontSize="text-xs"
                      inputSize="h-9"
                      showSearchIcon
                      isFloatingLabel={false}
                    />
                  </div>
                  <div className="w-[8rem] lg:w-[8.5rem] shrink-0 min-w-0">
                    <InputField
                      name="uhid"
                      control={control}
                      label="UHID"
                      fontSize="text-xs"
                      inputSize="h-9"
                    />
                  </div>
                  <div className="w-[9.5rem] lg:w-[8.75rem] shrink-0">
                    <CommonDatePicker
                      name="fromDate"
                      label="From Date"
                      control={control}
                      maxDate={watch("toDate")}
                      fontSize="text-xs"
                    />
                  </div>
                  <div className="w-[9.5rem] lg:w-[8.75rem] shrink-0">
                    <CommonDatePicker
                      name="toDate"
                      label="To Date"
                      control={control}
                      minDate={watch("fromDate")}
                      fontSize="text-xs"
                    />
                  </div>
                  <fieldset className="border border-gray-300 rounded px-2.5 py-1.5 shrink-0">
                    <RadioButtonGroup
                      name="patientType"
                      control={control}
                      options={PATIENT_TYPE_OPTIONS}
                      direction="horizontal"
                      noWrap
                      optionLabelClassName="text-xs"
                    />
                  </fieldset>
                  <fieldset className="border border-gray-300 rounded px-2.5 py-1.5 shrink-0">
                    <RadioButtonGroup
                      name="reportStatus"
                      control={control}
                      options={REPORT_STATUS_OPTIONS}
                      direction="horizontal"
                      noWrap
                      optionLabelClassName="text-xs"
                    />
                  </fieldset>
                  <div
                    className="hidden lg:block flex-1 min-w-2"
                    aria-hidden="true"
                  />
                  <CommonButton
                    label={loading ? "Loading..." : "Search"}
                    type="submit"
                    className="bg-[#217E90] hover:bg-[#1a5e70] text-white shrink-0 px-4"
                    size="sm"
                    disabled={loading}
                  />
                  <CommonButton
                    label="Reset"
                    type="button"
                    onClick={handleResetFilters}
                    variant="danger-outline"
                    size="sm"
                    className="shrink-0 px-3"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </form>

          <div className="min-h-0 flex flex-col overflow-hidden rounded-lg border border-gray-200 flex-1">
            <div className="px-3 py-2 border-b border-gray-200 bg-slate-50 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-xs sm:text-sm font-semibold text-[#217E90]">
                Pathology Test List
              </h3>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                <span>
                  Total:{" "}
                  <strong className="text-[#217E90]">{tableData.length}</strong>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded-sm bg-orange-200 border border-orange-400" />
                  Completed
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded-sm bg-green-200 border border-green-400" />
                  Printed
                </span>
              </div>
            </div>

            <div className="hidden lg:flex flex-1 min-h-0 flex-col overflow-hidden [&>div]:h-full [&>div]:min-h-0 [&>div]:flex [&>div]:flex-col [&>div]:border-0 [&>div]:rounded-none [&>div]:shadow-none [&_.overflow-auto]:!max-h-none [&_.overflow-auto]:flex-1">
              <EnhancedDynamicTablePagination
                dataResult={tableData}
                setDataResult={() => {}}
                fetchData={() => {}}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                count={tableData.length}
                removeHeaders={["id", "printed", "approved"]}
                columnsAfterActions={["reportStatus"]}
                renderCustomColumns={renderCustomColumns}
                columnHeaderLabels={{
                  date: "Date",
                  time: "Time",
                  uhid: "UHID",
                  patientName: "Patient Name",
                  testName: "Test Name",
                  doctorName: "Doctor Name",
                  reportStatus: "Status",
                }}
                highlightRow
                customRowBgColor="#F0F8FF"
                rowBackgroundColor={rowBackgroundColor}
                onRowClick={handleRowClick}
                tableClass="min-h-0"
                rowHoverEffect="highlight"
              />
            </div>

            <div className="block lg:hidden flex-1 min-h-0 overflow-y-auto space-y-2 p-2">
              {loading ? (
                <p className="text-center text-gray-500 py-8 text-sm">Loading...</p>
              ) : tableData.length === 0 ? (
                <p className="text-center text-gray-500 py-8 text-sm">
                  No pathology tests found
                </p>
              ) : (
                tableData.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => handleRowClick(row)}
                    className={`w-full text-left border rounded-lg p-3 ${
                      selectedId === row.id
                        ? "border-[#217E90] bg-[#F0F8FF]"
                        : "border-gray-200 hover:bg-[#F0F8FF]"
                    }`}
                  >
                    <p className="font-semibold text-sm text-[#217E90]">
                      {row.patientName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {row.testName} · {row.uhid} · {row.date} {row.time}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {row.doctorName} · {row.reportStatus}
                    </p>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-4 right-4 z-50 flex gap-2 sm:gap-3 pr-4 sm:pr-6">
        <CommonButton
          label={detailLoading ? "Loading..." : "Result Entry"}
          type="button"
          onClick={handleResultEntry}
          disabled={!hasSelection || detailLoading}
          variant="primary-filled"
          size="sm"
          className="flex-none px-3 md:px-5 text-xs md:text-sm bg-[#217E90] hover:bg-[#1a6575]"
        />
        <CommonButton
          label={approving ? "Approving..." : "Approve"}
          type="button"
          onClick={handleApprove}
          disabled={!hasSelection || isApproved || approving}
          variant="success-filled"
          size="sm"
          className="flex-none px-3 md:px-5 text-xs md:text-sm"
        />
      </div>

      <PathologyResultEntryModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        testRecord={selectedDetail}
        labs={labs}
        saving={saving}
        onSave={handleSaveResults}
      />
    </div>
  );
}

export default PathologyResultApproval;
